import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Landing card for /affiliates/dashboard/assets — shows the affiliate
// what's in the content hub, with a strong "Start Here" prompt for
// anyone who hasn't yet acknowledged the Compliance Framework.
const sections = [
  {
    to: "/affiliates/dashboard/assets/compliance",
    eyebrow: "★ START HERE",
    title: "Compliance Framework",
    italic: "first.",
    body: "Read this before posting anything. The rules, the brand voice, and the safety lines you can't cross — written plainly.",
    accent: true,
    testId: "hub-card-compliance",
  },
  {
    to: "/affiliates/dashboard/assets/playbooks",
    eyebrow: "★ PROGRAM PLAYBOOKS",
    title: "Program Playbooks",
    italic: "by program.",
    body: "Deep-dive guides for each VOYA9 program. Audience-fit, talking points, what to lean into, what to avoid.",
    testId: "hub-card-playbooks",
  },
  {
    to: "/affiliates/dashboard/assets/hooks",
    eyebrow: "★ QUICK REFERENCE",
    title: "Hooks",
    italic: "& conversation starters.",
    body: "Tested opening lines you can pluck and adapt. Filter by program or by audience angle.",
    testId: "hub-card-hooks",
  },
  {
    to: "/affiliates/dashboard/assets/posts",
    eyebrow: "★ QUICK REFERENCE",
    title: "Sample Posts",
    italic: "ready to adapt.",
    body: "Full post templates with disclaimers built in. One-tap copy to clipboard.",
    testId: "hub-card-posts",
  },
  {
    to: "/affiliates/dashboard/assets/benefit-cards",
    eyebrow: "★ QUICK REFERENCE",
    title: "Benefit Cards",
    italic: "for carousels.",
    body: "Headline + bullet snippets sized for IG carousels, reels, and graphics. Copy the whole card or just the bullets you need.",
    testId: "hub-card-benefit-cards",
  },
  {
    to: "/affiliates/dashboard/assets/story-ideas",
    eyebrow: "★ QUICK REFERENCE",
    title: "Story Ideas",
    italic: "to spark posts.",
    body: "Content angles and arc prompts — use them as the spine of a thread, layer in your voice on top.",
    testId: "hub-card-story-ideas",
  },
  {
    to: "/affiliates/dashboard/assets/comparison",
    eyebrow: "★ QUICK REFERENCE",
    title: "Comparison",
    italic: "vs. the usual.",
    body: "Head-to-head grids — what VOYA9 does vs. the typical alternative. Useful for 'vs.' style posts and FAQ replies.",
    testId: "hub-card-comparison",
  },
  {
    to: "/affiliates/dashboard/assets/faqs",
    eyebrow: "★ QUICK REFERENCE",
    title: "FAQ Responses",
    italic: "in your voice.",
    body: "How to answer the most common follower questions without overpromising.",
    testId: "hub-card-faqs",
  },
  {
    to: "/affiliates/dashboard/assets/disclaimers",
    eyebrow: "★ QUICK REFERENCE",
    title: "Disclaimer Library",
    italic: "use them.",
    body: "Every disclaimer you might need, with notes on when each one applies.",
    testId: "hub-card-disclaimers",
  },
];

export default function AssetsIndex() {
  // Lightweight ping for the compliance ack state so we can flag the
  // Start Here card when it's still pending. Silent fall-through if
  // BE hasn't shipped the endpoint yet.
  const [needsAck, setNeedsAck] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // BE exposes the foundational compliance content separately from
        // the universal /content endpoint; if it's already acknowledged
        // (or non-required) we don't surface the banner.
        const res = await affGet("/affiliate/content/foundational", { auth: true });
        const ack = res?.acknowledgement || (Array.isArray(res?.items) ? res.items[0]?.acknowledgement : null);
        if (!cancelled && ack?.required && !ack.acknowledgedAt) {
          setNeedsAck(true);
        }
      } catch {
        /* BE not live yet — no banner is fine. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ AFFILIATE CONTENT HUB</Eyebrow>
      <Headline before="EVERYTHING" italic="you need." size={56} className="mt-4" />
      <p style={{ marginTop: 18, fontSize: 16, color: C.warmGray, maxWidth: 640, lineHeight: 1.65 }}>
        Compliance, playbooks, hooks, and ready-to-adapt copy — built for the way you actually create content. Start with the framework, then dip in as you go.
      </p>

      {needsAck && (
        <div
          data-testid="hub-ack-banner"
          style={{
            marginTop: 28,
            padding: "18px 22px",
            background: C.peach,
            color: C.ink,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDark }}>
              ★ ONE STEP BEFORE YOU POST
            </div>
            <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700 }}>
              Please review and acknowledge the Compliance Framework.
            </div>
          </div>
          <Link
            to="/affiliates/dashboard/assets/compliance"
            data-testid="hub-ack-cta"
            style={{
              padding: "12px 18px",
              background: C.ink,
              color: C.peach,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.22em",
              textDecoration: "none",
            }}
          >
            READ & SIGN →
          </Link>
        </div>
      )}

      <div
        style={{
          marginTop: 36,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 18,
        }}
      >
        {sections.map((s) => <HubCard key={s.to} {...s} />)}
      </div>
    </div>
  );
}

function HubCard({ to, eyebrow, title, italic, body, accent, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      style={{
        textDecoration: "none",
        display: "block",
        background: accent ? C.ink : C.pure,
        color: accent ? C.cream : C.ink,
        border: accent ? "none" : `1px solid ${C.warmGray3}`,
        padding: "24px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 200,
        position: "relative",
        overflow: "hidden",
        transition: "transform 140ms ease, box-shadow 140ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: accent ? C.peach : C.peachDeep }}>
        {eyebrow}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.01em", marginTop: 4 }}>
        {title.toUpperCase()}{" "}
        <span style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 500, color: accent ? C.peach : C.peachDeep }}>
          {italic}
        </span>
      </div>
      <p style={{ marginTop: 6, fontSize: 13, lineHeight: 1.6, color: accent ? "rgba(250,246,240,0.78)" : C.warmGray, flex: 1 }}>
        {body}
      </p>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: accent ? C.peach : C.ink, marginTop: 8 }}>
        OPEN →
      </div>
    </Link>
  );
}
